import Constants from '../constants';

export default class StepDocsHelper {
  static determinePacketStatus(docs, stepStatus) {
    const needsRevisionDocsCount = docs.filter(
      ({ status }) => status === Constants.STEP_DOCUMENT_STATUS.NEEDS_REVISION
    ).length;
    if (needsRevisionDocsCount > 0) return Constants.STEP_DOCUMENT_STATUS.NEEDS_REVISION;

    if (stepStatus === Constants.STEP_STATUS.ACCEPTED) return Constants.STEP_DOCUMENT_STATUS.ACCEPTED;

    const docsAcceptedCount = docs.filter(
      ({ tier1Decision }) => tier1Decision === Constants.STEP_DOCUMENT_STATUS.ACCEPTED
    ).length;
    if (docsAcceptedCount === docs.length) return Constants.STEP_DOC_PACKET_STATUS.ACCEPTED;
    return null;
  }

  static determineRolesInPacketThatHaveSignatureStatus(signatureStatus, docs) {
    const roles = [];
    if (StepDocsHelper.doesAnyHomeownerSignatureStatusHaveValue(docs, signatureStatus))
      roles.push(Constants.VIRTUAL_SIGNER_ROLE_HOMEOWNER);
    if (StepDocsHelper.doesAnyInstallerSignatureStatusHaveValue(docs, signatureStatus))
      roles.push(Constants.VIRTUAL_SIGNER_ROLE_INSTALLER);
    return roles;
  }

  static determineRolesThatRequirePacketSignature(docs) {
    const roles = [];
    if (StepDocsHelper.isPacketHomeownerSignatureRequired(docs)) roles.push(Constants.VIRTUAL_SIGNER_ROLE_HOMEOWNER);
    if (StepDocsHelper.isPacketInstallerSignatureRequired(docs)) roles.push(Constants.VIRTUAL_SIGNER_ROLE_INSTALLER);
    return roles;
  }

  static doesAnyInFinancingRequestHaveStatus(financingRequest, status) {
    let result = false;
    financingRequest.steps.forEach((step) => {
      step.docs.forEach((doc) => {
        if (doc.status === status) result = true;
      });
    });
    return result;
  }

  static doesAnyHomeownerSignatureStatusHaveValue(docs, signatureStatus) {
    return (
      docs.filter(
        ({ signatureStatusHomeownerPrimarySigner }) => signatureStatusHomeownerPrimarySigner === signatureStatus
      ).length > 0
    );
  }

  static doesAnyInstallerSignatureStatusHaveValue(docs, signatureStatus) {
    return (
      docs.filter(({ signatureStatusInstallerSigner }) => signatureStatusInstallerSigner === signatureStatus).length > 0
    );
  }

  static doesAnyNotRequireHomeownerSignature(docs) {
    return docs.filter(({ signatureRequiredHomeowner }) => signatureRequiredHomeowner !== true).length > 0;
  }

  static doesAnyNotRequireInstallerSignature(docs) {
    return docs.filter(({ signatureRequiredUser }) => signatureRequiredUser !== true).length > 0;
  }

  static findHomeownerStepDocs(docs) {
    return StepDocsHelper.findByGroupName(docs, Constants.STEP_DOC_PACKET_GROUP_NAME_HOMEOWNER);
  }

  static findVisibleStepDocs(docs, excludeExternalSkus) {
    return docs.filter(
      ({ type, currentRevision, externalSku, groupName }) =>
        (type === Constants.STEP_DOCUMENT_TYPE.STANDARD || currentRevision !== null) &&
        (!excludeExternalSkus?.length || excludeExternalSkus.indexOf(externalSku) === -1) &&
        (!groupName || groupName === Constants.STEP_DOC_PACKET_GROUP_NAME_HOMEOWNER_PACKET)
    );
  }

  static findMultiFileStepDocs(docs, excludeExternalSkus) {
    return this.findVisibleStepDocs(docs, excludeExternalSkus)?.filter((doc) => doc?.maxFiles > 1);
  }

  static financingRequestHasDocBySku(financingRequest, docSku) {
    financingRequest?.steps?.forEach((step) =>
      step?.docs?.forEach((doc) => {
        if (doc.externalSku === docSku) return true;
      })
    );
    return false;
  }

  static findPacketGroupName(docs) {
    return docs[0].groupName;
  }

  static findByGroupName(docs, groupName) {
    return docs.filter(({ groupName: docGroupName }) => docGroupName === groupName);
  }

  static isStepDocGenerated(stepDoc) {
    const {
      STEP_DOCUMENT_STATUS: { GENERATED },
    } = Constants;
    return stepDoc.status === GENERATED;
  }

  static isHomeownerSignerSelectedButNotRequiredAndInstallerSignatureRequired(docs, signers) {
    const homeownerSignerSelected =
      signers.filter(
        ({ role }) =>
          role === Constants.SIGNER_ROLES.HOMEOWNER_PRIMARY_SIGNER ||
          role === Constants.SIGNER_ROLES.HOMEOWNER_SECONDARY_SIGNER
      ).length > 0;
    const homeownerSignatureNotRequired = StepDocsHelper.doesAnyNotRequireHomeownerSignature(docs);
    const installerSignatureNotRequired = StepDocsHelper.doesAnyNotRequireInstallerSignature(docs);
    return (
      homeownerSignerSelected === true &&
      homeownerSignatureNotRequired === true &&
      installerSignatureNotRequired === false
    );
  }

  static isInstallerSignerSelectedButNotRequiredAndHomeownerSignatureRequired(docs, signers) {
    const installerSignerSelected =
      signers.filter(({ role }) => role === Constants.SIGNER_ROLES.INSTALLER_SIGNER).length > 0;
    const homeownerSignatureNotRequired = StepDocsHelper.doesAnyNotRequireHomeownerSignature(docs);
    const installerSignatureNotRequired = StepDocsHelper.doesAnyNotRequireInstallerSignature(docs);
    return (
      installerSignerSelected === true &&
      installerSignatureNotRequired === true &&
      homeownerSignatureNotRequired === false
    );
  }

  static isPacketHomeownerSignatureRequired(docs) {
    if (!docs) {
      return false;
    }

    return (
      docs
        .filter(({ signatureRequiredHomeowner }) => signatureRequiredHomeowner === true)
        .filter(
          ({ signatureStatusHomeownerPrimarySigner }) =>
            Constants.SIGNED_SIGNATURE_STATUSES.indexOf(signatureStatusHomeownerPrimarySigner) === -1
        ).length > 0
    );
  }

  static isPacketOutForSignature(docs) {
    return (
      docs.filter((doc) => {
        const { currentRevision } = doc;
        return currentRevision?.currentEnvelopeId; // signatureStatus === Constants.SIGNATURE_STATUS.PENDING;
      }).length > 0
    );
  }

  static allSignableDocsAreGenerated(docs) {
    if (
      docs.some(
        ({ currentRevision, signatureRequiredHomeowner, signatureStatusHomeownerPrimarySigner }) =>
          (signatureRequiredHomeowner && signatureStatusHomeownerPrimarySigner === null) ||
          (signatureStatusHomeownerPrimarySigner === 'required' && (!currentRevision || !currentRevision?.fileId))
      )
    ) {
      return false; // don't treat signable docs as ready to sign until all have revisions, and signature statuses have all been set properly
    }
    return docs.some(
      ({ currentRevision, signatureStatusHomeownerPrimarySigner }) =>
        signatureStatusHomeownerPrimarySigner === 'required' && currentRevision?.creationMethod !== 'uploaded'
    );
  }

  static isPacketInstallerSignatureRequired(docs) {
    if (!docs) {
      return false;
    }

    return (
      docs
        .filter(({ signatureRequiredUser }) => signatureRequiredUser === true)
        .filter(
          ({ signatureStatusInstallerSigner }) =>
            Constants.SIGNED_SIGNATURE_STATUSES.indexOf(signatureStatusInstallerSigner) === -1
        ).length > 0
    );
  }

  static getHomeownerSignableDocIds(financingRequest, stepId) {
    let docs = financingRequest.steps
      .find(({ id }) => stepId === id)
      .docs.filter(
        ({ currentRevision, signatureStatusHomeownerPrimarySigner }) =>
          signatureStatusHomeownerPrimarySigner === 'required' && currentRevision?.creationMethod !== 'uploaded'
      )
      .map(({ currentRevision }) => currentRevision?.docId || -517);

    return docs;
  }

  static getSignableDocs(financingRequest, stepId) {
    return financingRequest.steps
      .find(({ id }) => stepId === id)
      .docs.filter(
        ({ currentRevision, signatureStatusHomeownerPrimarySigner, signatureRequiredUser }) =>
          currentRevision?.creationMethod !== 'uploaded' &&
          (signatureStatusHomeownerPrimarySigner === 'required' || signatureRequiredUser)
      );
  }

  static isReUploadable(stepDoc) {
    // should just be HIC (HomeImprovementContract sku)
    return stepDoc?.autoApprove && stepDoc?.formId === null;
  }

  static isRequestedStepDoc(stepDoc) {
    const cRevision = stepDoc?.currentRevision;
    return (
      stepDoc?.status === Constants.STEP_DOCUMENT_STATUS.NEEDED &&
      cRevision &&
      cRevision.creationMethod === 'slotted' &&
      cRevision.extension == null &&
      cRevision.name === 'Requested'
    );
  }
}
